import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";

type FilterCondition = "All" | "RA" | "SLE" | "SSc" | "axSpA" | "PsA";

const ADL_CATEGORIES = [
  {
    category: "Personal Care",
    icon: "🛁",
    conditions: ["All", "RA", "SLE", "SSc"],
    items: [
      {
        task: "Washing and bathing",
        challenge: "Reduced grip, painful shoulders, fatigue, limited reach",
        adaptations: ["Long-handled bath brush and sponge", "Bath board or bath seat", "Shower chair with padded armrests", "Lever taps (OT prescription)", "Liquid soap dispenser (avoid bar soap)", "Grab rails — referral to local authority OT if needed for home modification"],
        conditions: ["RA", "SSc", "SLE"],
      },
      {
        task: "Dressing",
        challenge: "Reduced finger dexterity, shoulder range, button fastening",
        adaptations: ["Dressing stick for lower body", "Long-handled shoe horn", "Elastic laces or Velcro fastenings", "Button hook / zip puller", "Sock aid", "Front-opening clothing recommended"],
        conditions: ["RA", "SSc", "SLE"],
      },
      {
        task: "Grooming (hair, teeth)",
        challenge: "Sustained shoulder flexion, grip, fine motor control",
        adaptations: ["Electric toothbrush (reduces grip force)", "Built-up toothbrush handles", "Long-handled comb / hairbrush", "Hair dryer stand", "Electric razor (reduces grip)"],
        conditions: ["RA", "SSc"],
      },
    ],
  },
  {
    category: "Kitchen & Home Management",
    icon: "🍳",
    conditions: ["All", "RA", "SLE", "SSc", "PsA"],
    items: [
      {
        task: "Cooking and food preparation",
        challenge: "Grip, wrist extension, jar opening, heavy pots",
        adaptations: ["Kettle tipper or lightweight kettle", "Electric can/jar opener", "Non-slip mat under boards and bowls", "Built-up handled cutlery and utensils", "Pre-cut and frozen vegetables to reduce chopping", "Trolley to transport items between worktop and table", "Perch stool to sit whilst preparing food"],
        conditions: ["RA", "SSc", "PsA"],
      },
      {
        task: "Cleaning",
        challenge: "Sustained grip, bending, fatigue",
        adaptations: ["Long-handled dustpan and brush", "Self-wringing mop", "Robotic hoover (prescription may be possible via AT pathway)", "Lightweight cleaning products", "Energy-conservation: break tasks into segments"],
        conditions: ["RA", "SLE"],
      },
      {
        task: "Laundry",
        challenge: "Heavy baskets, wringing, sustained grip",
        adaptations: ["Front-loading washing machine at accessible height", "Laundry trolley", "Avoid clothes lines — tumble dryer preferable", "Lightweight iron with resting stand"],
        conditions: ["RA", "SLE"],
      },
    ],
  },
  {
    category: "Work & Leisure",
    icon: "💼",
    conditions: ["All", "RA", "SLE", "axSpA", "PsA"],
    items: [
      {
        task: "Computer and desk work",
        challenge: "Sustained wrist position, grip, fatigue, concentration",
        adaptations: ["Ergonomic keyboard and mouse (vertical mouse, trackball)", "Voice recognition software", "Wrist support at keyboard", "Adjustable sit-stand desk", "Regular micro-breaks (Pomodoro technique)", "Monitor at eye level"],
        conditions: ["RA", "SLE", "axSpA"],
      },
      {
        task: "Writing",
        challenge: "Grip, sustained fine motor control",
        adaptations: ["Wide-grip pen (Yoropen, Pen Again, Skey pen)", "Pen holder splint", "Voice-to-text dictation", "Tablet with stylus"],
        conditions: ["RA", "SSc"],
      },
      {
        task: "Leisure (reading, crafts, hobbies)",
        challenge: "Sustained grip, posture, fatigue",
        adaptations: ["Book stand or e-reader", "Adapted knitting/craft aids", "Card holders for card games", "Gardening aids (adapted tools, raised beds)", "Rest planning around leisure activity"],
        conditions: ["RA", "SLE", "PsA"],
      },
    ],
  },
  {
    category: "Driving & Travel",
    icon: "🚗",
    conditions: ["All", "RA", "axSpA"],
    items: [
      {
        task: "Driving",
        challenge: "Grip on steering wheel, reaching, neck rotation (axSpA), fatigue",
        adaptations: ["Steering wheel cover (thicker grip)", "Swivel car seat cushion", "Lever extension for handbrake if needed", "DVLA referral for driving assessment at mobility centre if significant impairment (see GOV.UK guidance)", "Neck mirror for axSpA if neck rotation limited"],
        conditions: ["RA", "axSpA"],
      },
      {
        task: "Public transport",
        challenge: "Grip on poles, fatigue, prolonged standing",
        adaptations: ["Freedom Pass or bus pass (local authority eligibility)", "Seated passes on request", "Plan routes with rest points", "Disability assistance at rail stations (Passenger Assist)"],
        conditions: ["RA", "SLE"],
      },
    ],
  },
  {
    category: "Sexual Health & Relationships",
    icon: "❤️",
    conditions: ["RA", "SSc"],
    items: [
      {
        task: "Sexual activity",
        challenge: "Pain, fatigue, reduced mobility, body image, dryness (SSc/Sjögren's), reduced hand function",
        adaptations: ["Positioning aids and wedge cushions to reduce load on joints", "Timing sexual activity to best-energy periods and after analgesia", "Open communication with partner about limitations", "Referral to psychosexual counselling if relationship or body image concern", "Vaginal lubricants for dryness (SSc, Sjögren's — discuss with rheumatology nurse/GP)", "Raynaud's management before intimacy in SSc (warmth, avoiding cold)"],
        conditions: ["RA", "SSc"],
      },
      {
        task: "Body image and relationships",
        challenge: "Visible deformity, fatigue, reduced capacity, changed role",
        adaptations: ["Refer to clinical psychology if body image distress significant", "Peer support groups (NRAS, Versus Arthritis)", "Occupational therapy role in discussing relationship impact openly and non-judgementally"],
        conditions: ["RA", "SLE", "SSc"],
      },
    ],
  },
  {
    category: "Parenting with Rheumatic Conditions",
    icon: "👶",
    conditions: ["RA", "SLE"],
    items: [
      {
        task: "Infant care",
        challenge: "Lifting, bathing, carrying, sleep deprivation worsening fatigue",
        adaptations: ["Pram/pushchair with easy-open brake mechanisms", "Baby bath seat at height-adjusted table", "Front-loading nappy bag worn across body", "Carrier versus pram based on grip capacity", "Extra support from partner or carer during flares", "Occupational therapy review pre or post birth"],
        conditions: ["RA", "SLE"],
      },
      {
        task: "Childcare activities",
        challenge: "Floor play (RA hip/knee), sustained activities, fatigue",
        adaptations: ["Activities at table height instead of floor where possible", "Rest after school activities and during school day if employed", "School communication if parental capacity affects school run"],
        conditions: ["RA", "SLE"],
      },
    ],
  },
];

const COPM_GUIDANCE = {
  description: "The Canadian Occupational Performance Measure (COPM) is a semi-structured, client-centred outcome measure used to identify and prioritise occupational performance issues in self-care, productivity, and leisure.",
  steps: [
    "Step 1 — Identify problems: Ask the patient to identify occupational performance issues that they want, need, or are expected to do but cannot do satisfactorily. Use open questions across self-care, productivity, and leisure.",
    "Step 2 — Rate importance: Patient rates each problem 1–10 for importance to them. Select the top 5 most important problems.",
    "Step 3 — Score performance: For the top 5 problems, ask the patient to rate their current performance (1–10): 1 = unable to do it, 10 = able to do it extremely well.",
    "Step 4 — Score satisfaction: For the top 5 problems, ask satisfaction with current performance (1–10): 1 = not satisfied at all, 10 = extremely satisfied.",
    "Step 5 — Calculate scores: Sum performance scores / 5 = Performance score. Sum satisfaction scores / 5 = Satisfaction score. Range: 1–10 for each.",
    "Step 6 — Re-administer after intervention: MCID = ≥2 points change in performance or satisfaction score is clinically meaningful (Law et al., 1994).",
  ],
  scoring: "Performance: 1–10 (1 = unable, 10 = can do very well). Satisfaction: 1–10 (1 = not satisfied, 10 = extremely satisfied). Clinically meaningful change: ≥2 points.",
  notes: "The COPM is validated for use in rheumatology. It has strong client-centred face validity and is recommended by RCOT as a core OT outcome measure. It takes 20–40 minutes to administer.",
};

export default function ADL() {
  const [filter, setFilter] = useState<FilterCondition>("All");
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [showCOPM, setShowCOPM] = useState(false);

  const filteredCategories = ADL_CATEGORIES.filter((c) =>
    filter === "All" || c.conditions.includes(filter)
  );

  return (
    <OTLayout title="ADL & Functional Independence">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>Client-centred approach:</strong> ADL assessment in rheumatology OT should be occupation-focused and goal-directed, using the COPM or equivalent to identify what matters most to the patient, not a generic checklist.
        </div>

        {/* COPM */}
        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <button
            className="w-full px-6 py-4 text-left flex items-center justify-between"
            onClick={() => setShowCOPM(!showCOPM)}
          >
            <div>
              <h2 className="font-bold text-slate-900">COPM — Canadian Occupational Performance Measure</h2>
              <p className="text-sm text-slate-500 mt-0.5">Administration guide, scoring, and interpretation</p>
            </div>
            <span className="text-slate-400">{showCOPM ? "▲" : "▼"}</span>
          </button>
          {showCOPM && (
            <div className="px-6 pb-6 border-t border-slate-100 space-y-4">
              <p className="text-sm text-slate-700 mt-4">{COPM_GUIDANCE.description}</p>
              <div className="space-y-3">
                {COPM_GUIDANCE.steps.map((step, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                    <p className="text-sm text-slate-700">{step.split(" — ")[1] || step}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-700 mb-1">Scoring</p>
                <p className="text-sm text-amber-800">{COPM_GUIDANCE.scoring}</p>
              </div>
              <p className="text-xs text-slate-500 italic">{COPM_GUIDANCE.notes}</p>
            </div>
          )}
        </section>

        {/* ADL Categories */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">ADL Reference Guide</h2>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {(["All", "RA", "SLE", "SSc", "axSpA", "PsA"] as FilterCondition[]).map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === c ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filteredCategories.map((cat) => (
              <div key={cat.category} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                  onClick={() => setOpenCategory(openCategory === cat.category ? null : cat.category)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <h3 className="font-bold text-slate-900">{cat.category}</h3>
                  </div>
                  <span className="text-slate-400">{openCategory === cat.category ? "▲" : "▼"}</span>
                </button>

                {openCategory === cat.category && (
                  <div className="border-t border-slate-100">
                    {cat.items
                      .filter((i) => filter === "All" || i.conditions.includes(filter))
                      .map((item) => (
                        <div key={item.task} className="px-6 py-5 border-b border-slate-50 last:border-0">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <h4 className="font-semibold text-slate-900">{item.task}</h4>
                            <div className="flex flex-wrap gap-1 shrink-0">
                              {item.conditions.map((c) => (
                                <span key={c} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{c}</span>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mb-3"><span className="font-medium">Challenge:</span> {item.challenge}</p>
                          <ul className="space-y-1">
                            {item.adaptations.map((a) => (
                              <li key={a} className="text-sm text-slate-700 flex items-start gap-2">
                                <span className="text-amber-500 mt-1 shrink-0">✓</span>{a}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>
    </OTLayout>
  );
}
